import { useEffect, useState, useMemo } from "react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import Editable from "@/components/Editable";
import api from "@/lib/api";
import { ShoppingCart, Funnel } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const TYPE_LABEL = { physical: "Physical", digital: "Download", course: "Online course", class: "In-person" };

export default function Store() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/products").then((r) => setItems(r.data)); }, []);

  const types = useMemo(() => Array.from(new Set(items.map((i) => i.type))), [items]);
  const filtered = filter === "all" ? items : items.filter((i) => i.type === filter);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="public-store">
      <Seo title="Store | Puppl" description="Treats, training gear, online courses and in-person class spots — all in one shop." canonical={typeof window !== "undefined" ? window.location.origin + "/store" : ""} />
      <PublicHeader />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <Editable id="store.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-2" defaultHtml="Store" />
          <Editable id="store.heading" as="h1" className="font-heading text-4xl lg:text-5xl font-bold leading-tight" defaultHtml="Treats, gear &amp; training — one shop." />
          <Editable id="store.subtitle" as="p" className="text-lg opacity-90 mt-3 max-w-2xl" defaultHtml="Physical products, digital downloads, online courses and in-person class spots, all in one place." />
        </div>
      </section>

      <section className="py-10 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="flex flex-wrap gap-2 mb-8 items-center">
          <Funnel size={16} weight="duotone" className="text-[#f38838]" />
          <button onClick={() => setFilter("all")} className={`text-xs px-3 py-1.5 rounded-full font-semibold ${filter === "all" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`} data-testid="store-filter-all">All ({items.length})</button>
          {types.map((t) => (
            <button key={t} onClick={() => setFilter(t)} className={`text-xs px-3 py-1.5 rounded-full font-semibold ${filter === t ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`} data-testid={`store-filter-${t}`}>{TYPE_LABEL[t] || t}</button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="card-pp p-16 text-center text-stone-500">
            <ShoppingCart size={48} weight="duotone" className="mx-auto mb-3 text-stone-300" />
            Nothing in this category yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((p) => (
              <Link to={`/store/${p.slug}`} key={p.id} className="card-pp p-3 hover:-translate-y-1 transition-transform" data-testid={`store-card-${p.slug}`}>
                <div className="aspect-square bg-stone-100 rounded-xl mb-3 overflow-hidden">
                  {p.image_url ? <img loading="lazy" src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-stone-300"><ShoppingCart size={32} weight="duotone" /></div>}
                </div>
                <div className="px-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-stone-100 px-1.5 py-0.5 rounded text-stone-600">{TYPE_LABEL[p.type] || p.type}</span>
                    {p.stock !== null && p.stock !== undefined && p.stock <= 3 && p.stock > 0 && <span className="text-[10px] font-bold text-amber-700">Only {p.stock} left</span>}
                    {p.stock === 0 && <span className="text-[10px] font-bold text-red-600">Sold out</span>}
                  </div>
                  <h3 className="font-heading font-semibold text-stone-900 text-sm line-clamp-2">{p.name}</h3>
                  <div className="font-heading font-bold text-xl text-[#f38838] mt-1">${p.price}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
